import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../services/api';

const Profile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    whatsapp_number: '',
    phone_number: '',
  });
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getProfile();
      const profileData = response.data.profile;
      setProfile(profileData);
      setFormData({
        username: profileData.username,
        whatsapp_number: profileData.whatsapp_number || '',
        phone_number: profileData.phone_number || '',
      });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    setError('');
    setSuccess('');

    try {
      await userAPI.updateProfile(formData);
      setSuccess('Profile updated successfully!');
      fetchProfile(); // Refresh profile data
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Typography variant="h4" gutterBottom>
        Profile Settings
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', mr: 2 }}>
                  {profile?.username?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">{profile?.username}</Typography>
                  <Chip
                    label={profile?.user_type?.toUpperCase()}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  margin="normal"
                  required
                  disabled={updateLoading}
                />

                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  Support Contact Information
                </Typography>

                <TextField
                  fullWidth
                  label="WhatsApp Number (Support)"
                  name="whatsapp_number"
                  value={formData.whatsapp_number}
                  onChange={handleChange}
                  margin="normal"
                  required
                  disabled={updateLoading}
                  placeholder="e.g., +919876543210"
                  inputProps={{ maxLength: 15 }}
                  helperText="This number will be shown to users for WhatsApp support"
                />

                <TextField
                  fullWidth
                  label="Phone Number (Support)"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  margin="normal"
                  required
                  disabled={updateLoading}
                  placeholder="e.g., +919876543210"
                  inputProps={{ maxLength: 15 }}
                  helperText="This number will be shown to users for phone support"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={updateLoading}
                  sx={{ mt: 3 }}
                >
                  {updateLoading ? <CircularProgress size={24} /> : 'Update Profile'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  User ID
                </Typography>
                <Typography variant="body1">{profile?.id}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Account Type
                </Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                  {profile?.user_type}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={profile?.status}
                  color={profile?.status === 'active' ? 'success' : 'error'}
                  variant="filled"
                  size="small"
                />
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {new Date(profile?.created_at).toLocaleDateString()}
                </Typography>
              </Box>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                Support Contact Details
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  WhatsApp Number (Support)
                </Typography>
                <Typography variant="body1">
                  {profile?.whatsapp_number || 'Not set'}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Phone Number (Support)
                </Typography>
                <Typography variant="body1">
                  {profile?.phone_number || 'Not set'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </motion.div>
  );
};

export default Profile;
